/**
 * Versionierung des SiGe-Plans.
 *
 * Eine Version ist ein eigener Zeilenbaum unter `sige_projekte` — dieselbe
 * `project_id`, eigene Kinder. Diese Datei enthält ausschließlich die reine
 * Kopier-/Umschlüssellogik (ohne Supabase), damit sie testbar bleibt.
 *
 * Fremdschlüssel-Graph:
 *   sige_projekte.id
 *     ← sige_arbeitsschritte.sige_projekt_id
 *         ← sige_gefaehrdungen.arbeitsschritt_id
 *             ← sige_schutzmassnahmen.gefaehrdung_id
 *     ← sige_bloecke.sige_projekt_id
 *
 * Die Kopie vergibt alle neuen IDs im Client. Nur so ist die Umschlüsselung
 * eindeutig: würde man auf die von der Datenbank zurückgegebene Reihenfolge
 * eines Mehrzeilen-Inserts vertrauen, hinge die Zuordnung Kind→Elternteil an
 * einer nicht zugesicherten Eigenschaft.
 */

export type SigeRow = Record<string, any>;

export interface SigeVersionTree {
  project: SigeRow;
  arbeitsschritte: SigeRow[];
  gefaehrdungen: SigeRow[];
  schutzmassnahmen: SigeRow[];
  bloecke: SigeRow[];
}

/** Von der Datenbank verwaltete Spalten — dürfen nicht mitkopiert werden. */
export const GENERATED_COLUMNS = ['id', 'created_at', 'updated_at', 'deleted_at'] as const;

export const stripGeneratedColumns = (row: SigeRow): SigeRow => {
  const copy: SigeRow = {};
  Object.keys(row || {}).forEach(key => {
    if (!(GENERATED_COLUMNS as readonly string[]).includes(key)) {
      copy[key] = row[key];
    }
  });
  return copy;
};

/** Kinder umschlüsseln; Zeilen ohne bekanntes Elternteil fallen weg (Waisen). */
const remapChildren = (
  rows: SigeRow[],
  fkColumn: string,
  idMap: Record<string, string>,
  newId: () => string,
): { rows: SigeRow[]; idMap: Record<string, string> } => {
  const nextMap: Record<string, string> = {};
  const mapped: SigeRow[] = [];

  (rows || []).forEach(row => {
    const parentId = row?.[fkColumn];
    const newParentId = parentId ? idMap[parentId] : undefined;
    if (!newParentId) return;
    const id = newId();
    nextMap[row.id] = id;
    mapped.push({ ...stripGeneratedColumns(row), id, [fkColumn]: newParentId });
  });

  return { rows: mapped, idMap: nextMap };
};

/**
 * Baut aus dem geladenen Zeilenbaum eine vollständige Kopie mit neuen IDs.
 * Die Quelle wird nicht verändert.
 */
export const buildSigeVersionCopy = (
  source: SigeVersionTree,
  versionLabel: string,
  newId: () => string,
): SigeVersionTree => {
  const projectId = newId();
  const project: SigeRow = {
    ...stripGeneratedColumns(source.project),
    id: projectId,
    version_label: versionLabel,
  };

  const projectMap = { [source.project.id]: projectId };

  const steps = remapChildren(source.arbeitsschritte, 'sige_projekt_id', projectMap, newId);
  const hazards = remapChildren(source.gefaehrdungen, 'arbeitsschritt_id', steps.idMap, newId);
  const measures = remapChildren(source.schutzmassnahmen, 'gefaehrdung_id', hazards.idMap, newId);
  const blocks = remapChildren(source.bloecke, 'sige_projekt_id', projectMap, newId);

  return {
    project,
    arbeitsschritte: steps.rows,
    gefaehrdungen: hazards.rows,
    schutzmassnahmen: measures.rows,
    bloecke: blocks.rows,
  };
};

/** Anzeigename einer Version: eigener Name, sonst Anlagedatum. */
export const describeSigeVersion = (row: { version_label?: string | null; created_at?: string | null }): string => {
  const label = (row?.version_label || '').trim();
  if (label) return label;
  if (row?.created_at) {
    const d = new Date(row.created_at);
    if (!Number.isNaN(d.getTime())) {
      return `Stand ${d.toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })}`;
    }
  }
  return 'Ohne Bezeichnung';
};
